/**
 * "What a held caller hears" - the address, and a way to put a file behind it.
 *
 * Why it exists. This was one text field taking an absolute https URL, which
 * quietly assumed the operator already had their track hosted somewhere Twilio
 * could reach. Almost nobody does: the file is on their laptop, and "upload
 * this MP3 to a public web server first" is not a step a settings page gets to
 * demand. The button beside the field is that step, done here.
 *
 * Two presses, not one, and deliberately - the same rule the announcement clips
 * follow. Uploading stores the track and fills in the address; Save is what
 * makes a held customer hear it. The gap between them is where the operator
 * presses play and listens to what they just chose. A single button that did
 * both would put an unheard file on the phone.
 *
 * THE PLAYER DOES NOT LOAD THE FIELD'S VALUE, and that is not an oversight. An
 * uploaded track is stored as `/api/telephony/hold/<id>`, which serves Twilio a
 * looping document rather than audio; handing that to an `<audio>` element
 * fetches a few lines of XML and plays nothing. `holdPreviewUrl` is where that
 * distinction lives, along with the rest of the rules this component has no
 * business restating.
 */
import { useRef, useState } from "react";
import { AlertCircle, Music, RefreshCw, Upload, X } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { ApiError } from "../../../../lib/api";
import {
  uploadHoldMusic,
  type HoldMusicInfo,
  type StrayHoldMusic,
  type UploadedHoldMusic,
} from "../../../../lib/calls";
import { Btn } from "../../../ui";
import { fmtBytes } from "../../../ui/format";
import { Field, Note, Pill } from "../panel/fields";
import { holdInfoFor, holdPreviewUrl, holdSeconds, strayHoldMusic } from "./holdMusic";

/**
 * What each failure the endpoint can report is called on screen.
 *
 * Named one by one rather than shown as a code, for the reason the clip row
 * gives: these are different problems with different next steps - pick a
 * smaller file, convert it, or tell whoever runs the server. A single "upload
 * failed" leaves somebody pressing the same button with the same file.
 *
 * ⚠ THIS TABLE HAS TO COVER `holdmusic.routes.ts` COMPLETELY. A code the route
 * can send and this cannot name is a dead end dressed as a retry.
 */
const ERROR_KEYS: Record<string, string> = {
  no_file: "settings.telephony.hold.errEmpty",
  file_too_large: "settings.telephony.hold.errTooLarge",
  unsupported_type: "settings.telephony.hold.errType",
  encoder_missing: "settings.telephony.hold.errEncoder",
  encode_failed: "settings.telephony.hold.errEncoder",
  upload_failed: "settings.telephony.hold.errFailed",
};

/** What the file dialog offers. The two formats the route accepts, nothing else. */
const ACCEPT = "audio/mpeg,audio/wav,audio/x-wav,.mp3,.wav";

export function HoldMusicField({
  value,
  onChange,
  saved,
  stray,
}: {
  value: string;
  onChange: (next: string) => void;
  /** The upload behind the SAVED setting, as the last load described it. */
  saved: HoldMusicInfo | null;
  /** An uploaded track the SAVED setting does not point at. See `strayHoldMusic`. */
  stray: StrayHoldMusic | null;
}) {
  const t = useT();
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The track uploaded in this form and not saved yet. The server will not
  // report it until the card is saved, so without this the description under a
  // file the operator just chose would be the one it replaced.
  const [fresh, setFresh] = useState<UploadedHoldMusic | null>(null);

  const src = { saved, fresh, stray };
  const info = holdInfoFor(value, src);
  const preview = holdPreviewUrl(value, src);
  const unsaved = fresh !== null && info === fresh && saved?.id !== fresh.id;
  // A file that exists and that this field is not pointing at - the state a
  // production hold track spent an evening in, silently. See `strayHoldMusic`.
  const orphan = strayHoldMusic(value, src);
  // The same "here and inaudible" state as `unsaved`, reached by adopting the
  // track above rather than by uploading one. Saving is what ends both.
  const adopted = stray !== null && info === stray && saved?.id !== stray.id;

  async function handleFile(file: File) {
    setBusy(true);
    setError(null);
    try {
      const uploaded = await uploadHoldMusic(file);
      setFresh(uploaded);
      onChange(uploaded.holdUrl);
    } catch (err) {
      const code = err instanceof ApiError ? err.message : "";
      setError(ERROR_KEYS[code] ?? "settings.telephony.hold.errFailed");
    } finally {
      setBusy(false);
      // Cleared so that choosing the SAME file again still fires a change: the
      // input keeps its value otherwise, and a failed upload could not be
      // retried without picking a different track first.
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  return (
    /* A hairline block, matching the clip row under each announcement: this is
       the same kind of thing (a field with a way to fill it in and a player to
       check it), and the two should not look like different mechanisms. */
    <div className="grid gap-3 rounded-[20px] border border-[var(--crm-hair)] p-4">
      <Field label={t("settings.telephony.holdUrl")} hint={t("settings.telephony.holdUrlHint")}>
        <Pill
          value={value}
          onChange={onChange}
          placeholder="https://..."
          autoComplete="off"
          inputMode="url"
          mono
        />
      </Field>

      {/* Outside the Field on purpose: that one is a `label`, and a button
          inside a label has its clicks forwarded to the label's control - here
          that would mean the file dialog opening the URL box instead. */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
          }}
        />
        <Btn tone="dark" onClick={() => fileInput.current?.click()} disabled={busy}>
          {busy ? <RefreshCw className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
          {busy
            ? t("settings.telephony.hold.uploading")
            : info
              ? t("settings.telephony.hold.replace")
              : t("settings.telephony.hold.upload")}
        </Btn>
        {/* Only offered when there is something to clear. Emptying the field is
            a real choice and not a mistake: it is how an operator goes back to
            Twilio's own hold music. */}
        {value.trim() ? (
          <Btn
            onClick={() => {
              onChange("");
              setFresh(null);
              setError(null);
            }}
            disabled={busy}
          >
            <X className="size-3.5" />
            {t("settings.telephony.hold.clear")}
          </Btn>
        ) : null}
      </div>

      <span className="crm-sub block text-[11.5px] leading-snug">
        {t("settings.telephony.hold.hint")}
      </span>

      {/* Hearing it is the only check that counts. Same origin as this page, so
          a root-relative clip address resolves as it stands; the route is public
          because Twilio has to fetch it without a session. Nothing is loaded
          until somebody presses play.

          `color-scheme` is what darkens the browser's own player skin - nothing
          else in the app sets it, so without this the control stays a bright
          white pill on the dark card. */}
      {preview ? (
        <audio
          className="-mt-1 w-full max-w-[420px] [color-scheme:light] dark:[color-scheme:dark]"
          controls
          preload="none"
          src={preview}
        />
      ) : null}

      {info ? (
        <div className="crm-sub flex items-center gap-1.5 text-[11.5px] leading-snug">
          <Music className="size-3.5 shrink-0" />
          <span className="min-w-0 truncate">
            {info.label || t("settings.telephony.hold.unnamed")}
            {[holdSeconds(info.durationMs), fmtBytes(info.bytes)]
              .filter(Boolean)
              .map((part) => ` - ${part}`)
              .join("")}
          </span>
        </div>
      ) : null}

      {/* `warn`, not `ok`. Green reads as "done", and this state is the exact
          opposite: the file is here and not one caller is hearing it. That
          misreading is how a track ends up in the orphan note below. */}
      {unsaved || adopted ? (
        <Note tone="warn" live>{t("settings.telephony.hold.unsaved")}</Note>
      ) : null}

      {/* The recovery. Named with its size and filename rather than as "a
          previous upload", because the operator has to recognise it before
          pressing anything - the button writes it straight into the field. */}
      {orphan ? (
        <Note tone="warn" icon={<AlertCircle className="mt-0.5 size-4 shrink-0" />} live>
          <span className="block">
            {t("settings.telephony.hold.orphan", {
              file: orphan.label || t("settings.telephony.hold.unnamed"),
              size: fmtBytes(orphan.bytes),
            })}
          </span>
          <Btn
            small
            className="mt-2"
            onClick={() => {
              onChange(orphan.holdUrl);
              setError(null);
            }}
            disabled={busy}
          >
            <Music className="size-3.5" />
            {t("settings.telephony.hold.orphanUse")}
          </Btn>
        </Note>
      ) : null}

      {error ? (
        <Note tone="bad" live>
          {t(error)}
        </Note>
      ) : null}
    </div>
  );
}
